import type {
  BlastFrameAssumptions,
  InductionCoilAssumptions,
  OnPipeAssumptions,
} from "../models/types";

export type AssumptionGroup = "blastFrame" | "inductionCoil";

export interface AssumptionFieldMeta {
  id: string;
  group: AssumptionGroup;
  key: keyof BlastFrameAssumptions | keyof InductionCoilAssumptions;
  label: string;
  unit: string | null;
  usedIn: string;
  description: string;
  kind: "number" | "text";
}

export const ASSUMPTION_FIELD_META: AssumptionFieldMeta[] = [
  {
    id: "bf-smallHeadMaxSteelOdMm",
    group: "blastFrame",
    key: "smallHeadMaxSteelOdMm",
    label: "Head-size crossover OD",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Steel OD above which large heads replace small heads.",
    kind: "number",
  },
  {
    id: "bf-chamferSideClearanceMm",
    group: "blastFrame",
    key: "chamferSideClearanceMm",
    label: "Chamfer side clearance",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Minimum stand-off the blast frame must clear past the max tops-of-chamfer.",
    kind: "number",
  },
  {
    id: "bf-cwcOverallLengthMarginMm",
    group: "blastFrame",
    key: "cwcOverallLengthMarginMm",
    label: "CWC overall length margin",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Minimum running gap required either side of a concrete-weight-coating cutback.",
    kind: "number",
  },
  {
    id: "bf-smallHeadSealChordMm",
    group: "blastFrame",
    key: "smallHeadSealChordMm",
    label: "Small head seal chord",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Seal chord length used for small blast heads.",
    kind: "number",
  },
  {
    id: "bf-largeHeadSealChordMm",
    group: "blastFrame",
    key: "largeHeadSealChordMm",
    label: "Large head seal chord",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Seal chord length used for large blast heads.",
    kind: "number",
  },
  {
    id: "bf-twoTieBarHeadPlateWidthMm",
    group: "blastFrame",
    key: "twoTieBarHeadPlateWidthMm",
    label: "2-tie bar head plate width",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Head plate width assumed for 2-tie-bar blast frames.",
    kind: "number",
  },
  {
    id: "bf-fourTieBarHeadPlateWidthMm",
    group: "blastFrame",
    key: "fourTieBarHeadPlateWidthMm",
    label: "4-tie bar head plate width",
    unit: "mm",
    usedIn: "Blast Frame",
    description: "Head plate width assumed for 4-tie-bar blast frames.",
    kind: "number",
  },
  {
    id: "ic-chamferClearanceMm",
    group: "inductionCoil",
    key: "chamferClearanceMm",
    label: "Chamfer clearance margin",
    unit: "mm",
    usedIn: "Induction Coil",
    description: "Minimum stand-off the coil must clear past the max tops-of-chamfer.",
    kind: "number",
  },
  {
    id: "ic-cwcLengthMarginMm",
    group: "inductionCoil",
    key: "cwcLengthMarginMm",
    label: "CWC length margin",
    unit: "mm",
    usedIn: "Induction Coil",
    description: "Minimum running gap required either side of a concrete-weight-coating cutback.",
    kind: "number",
  },
  {
    id: "ic-coatingClearanceMm",
    group: "inductionCoil",
    key: "coatingClearanceMm",
    label: "Coil clearance margin",
    unit: "mm",
    usedIn: "Induction Coil",
    description: "Margin the coil inside diameter must clear past the max total coating OD.",
    kind: "number",
  },
  {
    id: "ic-stepDownCoatingThicknessMm",
    group: "inductionCoil",
    key: "stepDownCoatingThicknessMm",
    label: "Coil step-down threshold",
    unit: "mm",
    usedIn: "Induction Coil",
    description: "Single-side coating build-up above which a stepped coil section is recommended.",
    kind: "number",
  },
  {
    id: "ic-preferredNonCwcVersionId",
    group: "inductionCoil",
    key: "preferredNonCwcVersionId",
    label: "Preferred non-CWC version",
    unit: null,
    usedIn: "Induction Coil",
    description: "Default bottom-end version preferred when concrete weight coating is not present.",
    kind: "text",
  },
];

export function cloneAssumptions(values: OnPipeAssumptions): OnPipeAssumptions {
  return {
    blastFrame: { ...values.blastFrame },
    inductionCoil: { ...values.inductionCoil },
  };
}

export function getAssumptionDisplayValue(
  assumptions: OnPipeAssumptions,
  meta: AssumptionFieldMeta,
): string {
  const group = assumptions[meta.group];
  const value = group[meta.key as keyof typeof group];
  return value == null ? "" : String(value);
}

export function setAssumptionValue(
  assumptions: OnPipeAssumptions,
  meta: AssumptionFieldMeta,
  value: string | number,
): OnPipeAssumptions {
  const next = cloneAssumptions(assumptions);
  if (meta.group === "blastFrame") {
    next.blastFrame = {
      ...next.blastFrame,
      [meta.key]: value,
    } as BlastFrameAssumptions;
  } else {
    next.inductionCoil = {
      ...next.inductionCoil,
      [meta.key]: value,
    } as InductionCoilAssumptions;
  }
  return next;
}
